import { api } from "@/lib/api-client";
import { TextVariant } from "@/types/text-variants";
import {
  Accordion,
  AccordionItem,
  addToast,
  Button,
  Divider,
} from "@heroui/react";
import { Text } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { RiMoneyEuroCircleLine, RiTimeLine } from "react-icons/ri";
import { z } from "zod";
import {
  formatCurrency,
  returnCurrencySymbol,
} from "../../../../../shared/utils/utils";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import useFeature from "@/hooks/useFeature";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { t } from "@lingui/core/macro";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";
import { LimeCollapse } from "@/Components/NextBase/LimeCollapse";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";

const customValuesSchema = z.object({
  customValues: z.array(
    z.object({
      serviceId: z.number(),
      customDuration: z.number().nullable(),
      customPrice: z.number().nullable(),
      customTimeOffStart: z.number().nullable(),
      customTimeOffDuration: z.number().nullable(),
    }),
  ),
});

const ManageCustomValuesForServices = ({
  userId,
  setIsManagingCustomValuesForServices,
}: {
  userId: number;
  setIsManagingCustomValuesForServices: (value: boolean) => void;
}) => {
  const { data: customValues } = api.user.useGetUserServiceCustomValues({
    params: {
      userId,
    },
  });

  const { mutateAsync: putUserServiceCustomValues, processedErrorMessage } =
    api.user.usePutUserServiceCustomValues({
      userId,
    });

  const { data: servicesData } = api.service.useGetServices({});

  const form = useForm<z.infer<typeof customValuesSchema>>({
    initialValues: {
      customValues: [],
    },
    validate: zodResolver(customValuesSchema),
  });

  useEffect(() => {
    if (customValues) {
      form.initialize({
        customValues: customValues.customValues,
      });
    }
  }, [customValues]);

  const { isFeatureEnabled } = useFeature();

  const onSubmit = async (values: z.infer<typeof customValuesSchema>) => {
    await putUserServiceCustomValues(values);

    addToast({
      title: t`Uspešno shranjeno`,
      description: t`Cene in trajanja so bile uspešno shranjene`,
      color: "success",
    });
  };

  if (!customValues || !servicesData) return null;

  return (
    <div>
      <form onSubmit={form.onSubmit(onSubmit)}>
        <LimeAlert className="mb-2" message={processedErrorMessage}></LimeAlert>
        <Accordion variant="bordered" className="w-full">
          {(customValues?.customValues || []).map((cv, index) => {
            const service = servicesData.services.find(
              (s) => s.serviceId === cv.serviceId,
            );

            if (!service) return null;

            const customServiceRow = form
              .getValues()
              .customValues.find((cv) => cv.serviceId === service.serviceId);

            if (!customServiceRow) return null;

            const duration =
              customServiceRow.customDuration || service.baseDurationMins;

            const price =
              customServiceRow.customPrice || service.priceBaseCents / 100;

            const isCustomTimeOff =
              customServiceRow.customTimeOffDuration &&
              customServiceRow.customTimeOffStart;

            return (
              <AccordionItem
                title={
                  <div className="flex items-center justify-between">
                    <p className="font-semibold"> {service.name}</p>
                    <div className="flex flex-col items-end justify-end">
                      <div className="flex items-center gap-2">
                        <RiTimeLine />

                        {customServiceRow.customDuration && (
                          <motion.div
                            initial={{ opacity: 0, width: "auto" }}
                            animate={{ opacity: 1, width: "auto" }}
                            exit={{ opacity: 0, width: "auto" }}
                            transition={{ duration: 0.5 }}
                            className="text-sm line-through"
                          >
                            {service.baseDurationMins} min
                          </motion.div>
                        )}

                        <div className="text-sm">{duration} min</div>
                      </div>

                      <div className="flex items-center gap-2">
                        <RiMoneyEuroCircleLine />

                        {customServiceRow.customPrice && (
                          <motion.div
                            initial={{ opacity: 0, width: "auto" }}
                            animate={{ opacity: 1, width: "auto" }}
                            exit={{ opacity: 0, width: "auto" }}
                            transition={{ duration: 0.5 }}
                            className="text-sm line-through"
                          >
                            {formatCurrency({
                              amount: service.priceBaseCents / 100,
                              currency: "EUR",
                              locale: i18n.locale,
                            })}{" "}
                          </motion.div>
                        )}

                        <div className="text-sm">
                          {formatCurrency({
                            amount: price,
                            currency: "EUR",
                            locale: i18n.locale,
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                }
                key={service.serviceId}
                value={service.name}
                className={"accordion_item"}
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <Divider />

                    <div className="flex items-center justify-between">
                      <span className="font-semibold">
                        <Trans>Urejanje cene</Trans>
                      </span>
                      <Button
                        variant={"shadow"}
                        size="sm"
                        onPress={() => {
                          form.setFieldValue(
                            `customValues.${index}.customPrice`,
                            null,
                          );
                        }}
                      >
                        <Trans> Ponastavi ceno</Trans>
                      </Button>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="flex shrink-0">
                        <Text variant={TextVariant.Body}>
                          <Trans>Redna cena:</Trans>{" "}
                          {formatCurrency({
                            amount: service.priceBaseCents / 100,
                            currency: "EUR",
                            locale: i18n.locale,
                          })}
                        </Text>
                      </div>
                      <div>
                        <LimeSwitch
                          checked={customServiceRow.customPrice !== null}
                          title={t`Cena po meri`}
                          isCompact
                          onValueChange={(e) => {
                            if (e) {
                              form.setFieldValue(
                                `customValues.${index}.customPrice`,
                                service.priceBaseCents / 100,
                              );
                            } else {
                              form.setFieldValue(
                                `customValues.${index}.customPrice`,
                                null,
                              );
                            }
                          }}
                        ></LimeSwitch>
                      </div>
                    </div>
                    <LimeCollapse
                      isOpen={customServiceRow.customPrice !== null}
                    >
                      <LimeNumberInput
                        step={1}
                        minValue={0}
                        label={t`Cena po meri`}
                        size="sm"
                        hideStepper
                        {...form.getInputProps(
                          `customValues.${index}.customPrice`,
                        )}
                        endContent={returnCurrencySymbol({
                          currency: "EUR",
                        })}
                      ></LimeNumberInput>{" "}
                    </LimeCollapse>
                  </div>

                  <Divider></Divider>

                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">
                        <Trans>Urejanje trajanja</Trans>
                      </span>
                      <Button
                        variant={"shadow"}
                        size="sm"
                        onPress={() => {
                          form.setFieldValue(
                            `customValues.${index}.customDuration`,
                            null,
                          );

                          form.setFieldValue(
                            `customValues.${index}.customTimeOffStart`,
                            null,
                          );

                          form.setFieldValue(
                            `customValues.${index}.customTimeOffDuration`,
                            null,
                          );
                        }}
                      >
                        <Trans>Ponastavi trajanje</Trans>
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex w-full flex-col gap-4">
                        <Text variant={TextVariant.Body}>
                          <Trans>Redno trajanje:</Trans>
                        </Text>
                        {service.timeOffStart && service.timeOffDuration ? (
                          <div className="flex w-full justify-between gap-4">
                            <div className="flex w-1/3 flex-col items-center rounded-md bg-gray-100 p-1">
                              <span className="text-sm text-gray-500">
                                <Trans>Začetno trajanje</Trans>:
                              </span>
                              {service.timeOffStart}
                            </div>
                            <div className="flex w-1/3 flex-col items-center rounded-md bg-gray-100 p-1">
                              <div className="text-sm text-gray-500">
                                <Trans>Trajanje brez izvajalca</Trans>:
                              </div>
                              {service.timeOffDuration}
                            </div>
                            <div className="flex w-1/3 flex-col items-center rounded-md bg-gray-100 p-1">
                              <div className="text-sm text-gray-500">
                                <Trans>Končno trajanje</Trans>:
                              </div>
                              {service.baseDurationMins -
                                service.timeOffStart -
                                service.timeOffDuration}
                            </div>
                          </div>
                        ) : (
                          <div>{service.baseDurationMins} min</div>
                        )}
                      </div>
                    </div>
                    <div className="w-fit">
                      <LimeSwitch
                        checked={customServiceRow.customDuration !== null}
                        title={t`Trajanje po meri`}
                        isCompact
                        onValueChange={(e) => {
                          if (e) {
                            form.setFieldValue(
                              `customValues.${index}.customDuration`,
                              service.baseDurationMins,
                            );

                            form.setFieldValue(
                              `customValues.${index}.customTimeOffStart`,
                              service.timeOffStart,
                            );

                            form.setFieldValue(
                              `customValues.${index}.customTimeOffDuration`,
                              service.timeOffDuration,
                            );
                          } else {
                            form.setFieldValue(
                              `customValues.${index}.customDuration`,
                              null,
                            );
                          }
                        }}
                      ></LimeSwitch>
                    </div>

                    <LimeCollapse
                      isOpen={customServiceRow.customDuration !== null}
                    >
                      <div className="flex flex-col gap-4">
                        <div className="flex gap-4">
                          <div className="flex flex-1 items-center">
                            <span>
                              <Trans>Trajanje po meri</Trans>:
                            </span>
                          </div>
                          {isFeatureEnabled("TIMEOFF") ? (
                            <LimeSwitch
                              className="flex-1"
                              title={t`Trajanje brez izvajalca`}
                              checked={isCustomTimeOff}
                              onValueChange={(e) => {
                                if (e) {
                                  form.setFieldValue(
                                    `customValues.${index}.customTimeOffStart`,
                                    service.timeOffStart || 1,
                                  );
                                  form.setFieldValue(
                                    `customValues.${index}.customTimeOffDuration`,
                                    service.timeOffDuration || 1,
                                  );
                                } else {
                                  form.setFieldValue(
                                    `customValues.${index}.customTimeOffStart`,
                                    null,
                                  );
                                  form.setFieldValue(
                                    `customValues.${index}.customTimeOffDuration`,
                                    null,
                                  );
                                }
                              }}
                            ></LimeSwitch>
                          ) : null}
                        </div>

                        <div className="flex flex-col justify-between gap-4 md:flex-row md:gap-0">
                          <LimeNumberInput
                            label={
                              isCustomTimeOff
                                ? t`Začetno trajanje`
                                : t`Trajanje`
                            }
                            step={1}
                            minValue={1}
                            className="max-w-1/4 md:w-fit"
                            value={
                              isCustomTimeOff
                                ? customServiceRow.customTimeOffStart
                                : duration
                            }
                            size="sm"
                            onChange={(value) => {
                              if (typeof value !== "number") return;

                              if (isCustomTimeOff) {
                                form.setFieldValue(
                                  `customValues.${index}.customTimeOffStart`,
                                  value,
                                );
                              } else {
                                form.setFieldValue(
                                  `customValues.${index}.customDuration`,
                                  value,
                                );
                              }
                            }}
                            endContent={
                              <Text variant={TextVariant.Caption}>min</Text>
                            }
                          ></LimeNumberInput>

                          <AnimatePresence>
                            {isCustomTimeOff ? (
                              <motion.div
                                initial={{ opacity: 0, width: "auto" }}
                                animate={{ opacity: 1, width: "auto" }}
                                exit={{ opacity: 0, width: "auto" }}
                                transition={{ duration: 0.2 }}
                              >
                                <LimeNumberInput
                                  label={t`Trajanje brez izvajalca`}
                                  step={1}
                                  minValue={1}
                                  value={customServiceRow.customTimeOffDuration}
                                  size="sm"
                                  onChange={(value) => {
                                    if (typeof value !== "number") return;

                                    form.setFieldValue(
                                      `customValues.${index}.customTimeOffDuration`,
                                      value,
                                    );
                                  }}
                                  endContent={
                                    <Text variant={TextVariant.Caption}>
                                      min
                                    </Text>
                                  }
                                ></LimeNumberInput>
                              </motion.div>
                            ) : null}
                          </AnimatePresence>

                          <AnimatePresence>
                            {isCustomTimeOff ? (
                              <motion.div
                                initial={{ opacity: 0, width: "auto" }}
                                animate={{ opacity: 1, width: "auto" }}
                                exit={{ opacity: 0, width: "auto" }}
                                transition={{ duration: 0.2 }}
                              >
                                <LimeNumberInput
                                  label={t`Končno trajanje`}
                                  step={1}
                                  // minValue={1}
                                  value={
                                    duration -
                                    (customServiceRow.customTimeOffDuration ||
                                      0) -
                                    (customServiceRow.customTimeOffStart || 0)
                                  }
                                  size="sm"
                                  onChange={(value) => {
                                    if (typeof value !== "number") return;

                                    const totalDuration =
                                      value +
                                      (customServiceRow.customTimeOffDuration ||
                                        0) +
                                      (customServiceRow.customTimeOffStart ||
                                        0);

                                    form.setFieldValue(
                                      `customValues.${index}.customDuration`,
                                      totalDuration,
                                    );
                                  }}
                                  endContent={
                                    <Text variant={TextVariant.Caption}>
                                      min
                                    </Text>
                                  }
                                ></LimeNumberInput>
                              </motion.div>
                            ) : null}
                          </AnimatePresence>
                        </div>
                      </div>
                    </LimeCollapse>
                  </div>
                </div>
              </AccordionItem>
            );
          })}
        </Accordion>

        <div className="flex-start md:flex-end flex gap-4 pt-4 md:justify-end">
          <Button
            onPress={() => {
              setIsManagingCustomValuesForServices(false);
            }}
          >
            <Trans>Nazaj</Trans>
          </Button>
          <Button color="primary" type="submit">
            <Trans>Shrani</Trans>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ManageCustomValuesForServices;
